import { useMemo } from 'react';

import {
    DragEndEvent,
    DragOverEvent,
    UniqueIdentifier,
    useSensors,
    MouseSensor,
    TouchSensor,
    useSensor,
    pointerWithin,
    rectIntersection,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

import { DreConfiguracao } from '../../types';

import { useDreConfigConfiguracoesContext } from 'pages/financeiro/dre/configuracao/contexts/DreConfigConfiguracoesContext';
import { useSortConfiguracoes } from 'pages/financeiro/dre/configuracao/hooks';

/**
 * Sensor de colisão customizado para detectar o ponteiro do mouse.
 */
const customCollisionDetectionAlgorithm = (args: any) => {
    const pointerCollisions = pointerWithin(args);

    if (pointerCollisions.length > 0) {
        return pointerCollisions;
    }

    return rectIntersection(args);
};

/**
 * Retorna um map com as linhas de configurações.
 */
const getLines = (configuracoes: DreConfiguracao[]) => {
    const lines = new Map<number, DreConfiguracao[]>();

    configuracoes
        .sort((a, b) => a.nrSequencia - b.nrSequencia)
        .forEach((item) => {
            const containerId = Math.floor(item.nrSequencia / 10);

            if (!lines.has(containerId)) {
                lines.set(containerId, []);
            }

            lines.get(containerId)?.push(item);
        });

    // identifica a última linha e adiciona uma nova linha vazia
    const lastKey = Math.max(...Array.from(lines.keys()));
    lines.set(lastKey + 1, []);

    return lines;
};

/**
 * Retorna um map com as configurações, onde a chave é o id da configuração.
 */
const getMapFromConfiguracoes = (configuracoes: DreConfiguracao[]) =>
    new Map<UniqueIdentifier, DreConfiguracao>(configuracoes.map((item) => [item.idDreMercadologicaConfiguracao, item]));

/**
 * Retorna um novo array de configurações com a sequência atualizada.
 */
const getNewSequenceConfiguracoes = (configuracoes: DreConfiguracao[], lineId: number) =>
    configuracoes.map((config, index) => ({
        ...config,
        nrSequencia: lineId * 10 + index + 1,
    }));

const isConfiguracao = (id: UniqueIdentifier) => String(id).startsWith('configuracao-');
const getIdFromConfiguracao = (id: UniqueIdentifier) => Number(String(id).split('-')[1]);

const isLine = (id: UniqueIdentifier) => String(id).startsWith('line-');
const getIdFromLine = (id: UniqueIdentifier) => Number(String(id).split('-')[1]);

export function useDragConfiguracoes() {
    const { configuracoes, setConfiguracoes } = useDreConfigConfiguracoesContext();
    const { updateSort } = useSortConfiguracoes();

    // Configurações de sensores para mouse e toque
    const sensors = useSensors(
        useSensor(MouseSensor),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 5,
            },
        }),
    );

    /**
     * useMemo que retorna um objeto com as linhas de configurações.
     */
    const lines: Map<number, DreConfiguracao[]> = useMemo(() => getLines(configuracoes), [configuracoes]);

    /**
     * Encontra a linha que possui o id da configuração passado como parâmetro.
     */
    const findLineFromIdConfig = (id: UniqueIdentifier) => {
        for (const [lineId, configs] of lines.entries()) {
            if (configs.some((item) => item.idDreMercadologicaConfiguracao === id)) {
                return lineId;
            }
        }

        return -1;
    };

    /**
     * Reordena as configurações arrastadas dentro da mesma linha.
     */
    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        if (!over) return;

        // obtém os ids dos elementos arrastados
        const activeId = getIdFromConfiguracao(active.id);
        const overId = getIdFromConfiguracao(over.id);

        if (activeId === overId) return;

        // obtém a linha que contém o elemento arrastado e suas configs
        const lineId = findLineFromIdConfig(activeId);
        const lineConfiguracoes = lines.get(lineId) as DreConfiguracao[];

        // descobre o índice do elemento arrastado e do elemento sobre o qual ele foi solto
        // realiza a troca de posição entre os dois elementos
        const activeIndex = lineConfiguracoes.findIndex((item) => item.idDreMercadologicaConfiguracao === activeId);
        const overIndex = lineConfiguracoes.findIndex((item) => item.idDreMercadologicaConfiguracao === overId);
        const sortedConfiguracoes = arrayMove(lineConfiguracoes, activeIndex, overIndex);

        // atualiza a sequência de cada configuração da linha
        // cria um map com as configurações da linha reordenada para facilitar a atualização
        // cria um novo array de configurações com as configurações reordenadas
        const formattedConfiguracoes = getNewSequenceConfiguracoes(sortedConfiguracoes, lineId);
        const formattedConfiguracoesMap = getMapFromConfiguracoes(formattedConfiguracoes);
        const newConfiguracoes = configuracoes.map((config) => formattedConfiguracoesMap.get(config.idDreMercadologicaConfiguracao) || config);

        setConfiguracoes(newConfiguracoes);

        updateSort(newConfiguracoes);
    };

    const dragOverConfiguracao = ({ active, over }: DragOverEvent) => {
        if (!over) return;

        const activeId = getIdFromConfiguracao(active.id);
        const overId = getIdFromConfiguracao(over.id);

        const activeLineId = findLineFromIdConfig(activeId);
        const overLineId = findLineFromIdConfig(overId);

        const changedLine = activeLineId !== overLineId;

        if (changedLine) {
            const activeLineConfigs = lines.get(activeLineId) as DreConfiguracao[];
            const overLineConfigs = lines.get(overLineId) as DreConfiguracao[];

            // descobre o índice do elemento arrastado e do elemento sobre o qual ele foi solto
            const activeIndex = activeLineConfigs.findIndex((item) => item.idDreMercadologicaConfiguracao === activeId);
            const overIndex = overLineConfigs.findIndex((item) => item.idDreMercadologicaConfiguracao === overId);

            // remove o elemento arrastado da linha ativa
            // atualiza a sequência de cada configuração da linha ativa
            // cria um map com as configurações da linha ativa
            const filteredActiveLine = activeLineConfigs.filter((item) => item.idDreMercadologicaConfiguracao !== activeId);
            const formattedActiveLine = getNewSequenceConfiguracoes(filteredActiveLine, activeLineId);
            const formattedActiveLineMap = getMapFromConfiguracoes(formattedActiveLine);

            // adiciona o item arrastado na linha e na mesma posição da nova linha
            // atualiza a sequência de cada configuração da nova linha
            // cria um map com as configurações da nova linha
            overLineConfigs.splice(overIndex, 0, activeLineConfigs[activeIndex]);
            const formattedOverLine = getNewSequenceConfiguracoes(overLineConfigs, overLineId);
            const formattedOverLineMap = getMapFromConfiguracoes(formattedOverLine);

            // cria um novo array de configurações com as configurações reordenadas das linhas alteradas
            const newConfiguracoes = configuracoes.map((config) => {
                const { idDreMercadologicaConfiguracao } = config;
                const activeLineItem = formattedActiveLineMap.get(idDreMercadologicaConfiguracao);
                const overLineItem = formattedOverLineMap.get(idDreMercadologicaConfiguracao);

                return activeLineItem || overLineItem || config;
            });

            setConfiguracoes(newConfiguracoes);

            updateSort(newConfiguracoes);
        }
    };

    /**
     * Reordena as configurações arrastadas no espaço vazio de uma linha diferente.
     * Caso arraste para a última linha, adiciona uma nova linha para o item,
     * apenas se esse item já não for um item único da última linha.
     */
    const dragOverLine = ({ active, over }: DragOverEvent) => {
        if (!over) return;

        const activeId = getIdFromConfiguracao(active.id);
        const activeLineId = findLineFromIdConfig(activeId);

        const overLineId = getIdFromLine(over.id);
        const changedLine = activeLineId !== overLineId;

        if (changedLine) {
            // obtem as configs das linhas
            const activeLineConfigs = lines.get(activeLineId) as DreConfiguracao[];
            const overLineConfigs = lines.get(overLineId) as DreConfiguracao[];

            // caso a ultima linha com configs for a ativa e possui apenas um item
            // e estive arrastando para a ultima linha
            // retorna e não faz nada, para não recriar novas linhas desnecessariamente
            const linesIds = [...lines.keys()];
            const lastLineId = linesIds.at(-1);
            const lastLineWithConfigId = linesIds.at(-2);
            const isOveringNewLine = overLineId === lastLineId;
            const isLastLineWithConfig = lastLineWithConfigId === activeLineId;

            if (isOveringNewLine && isLastLineWithConfig && activeLineConfigs.length === 1) {
                return;
            }

            // remove o elemento arrastado da linha ativa
            // atualiza a sequência de cada configuração da linha ativa
            // cria um map com as configurações da linha ativa
            const filteredActiveLine = activeLineConfigs.filter((item) => item.idDreMercadologicaConfiguracao !== activeId);
            const formattedActiveLine = getNewSequenceConfiguracoes(filteredActiveLine, activeLineId);
            const formattedActiveLineMap = getMapFromConfiguracoes(formattedActiveLine);

            // descobre a nova sequencia para o item arrastado.
            // se é a última linha, adiciona o item na nova linha
            // caso contrário, adiciona o item no final da linha
            const overLineLastItem = overLineConfigs?.at(-1)?.nrSequencia;
            const newSequencia = overLineLastItem ? overLineLastItem + 1 : overLineId * 10 + 1;

            const newConfiguracoes = configuracoes.map((config) => {
                const { idDreMercadologicaConfiguracao } = config;

                if (idDreMercadologicaConfiguracao === activeId) {
                    return { ...config, nrSequencia: newSequencia };
                }

                return formattedActiveLineMap.get(idDreMercadologicaConfiguracao) || config;
            });

            updateSort(newConfiguracoes);

            setConfiguracoes(newConfiguracoes);
        }
    };

    /**
     * Reordena as configurações arrastadas entre linhas diferentes.
     */
    const handleDragOver = (event: DragOverEvent) => {
        if (!event.over) return;

        if (isConfiguracao(event.over.id)) {
            dragOverConfiguracao(event);
        } else if (isLine(event.over.id)) {
            dragOverLine(event);
        }
    };

    return { handleDragEnd, handleDragOver, lines, getIdFromConfiguracao, sensors, customCollisionDetectionAlgorithm };
}
